import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Select from "../generic-ui/Select";
import SearchBack from "../generic-search/SearchBack";
import { useTracking } from "react-tracking";
import { useTranslation } from "react-i18next";
import NavbarIconsModuleContext from "../generic-nav-bar/NavbarIconsModuleContext";
import {
  cleanTechnologiesPaginatedS,
  selectTechnology,
  startLoadingTechnologiesPaginated,
  startSearchingTechnologiesPaginated,
  technologyCleanSearchText,
  technologyStartDelete
} from "../../app/actions/technologyActions";
import { startLoadingCatalogsAll } from "../../app/actions/catalogActions";
import { startLoadingCatalogCiiusAll, startLoadingCatalogCiiusByLevel1 } from "../../app/actions/catalogCiiuActions";
import { startLoadingContinents } from "../../app/actions/continentActions";
import { getContinentRegionCountryByCountryId, startLoadingCountries } from "../../app/actions/countryActions";
import { startLoadingRegions } from "../../app/actions/regionActions";
import TableTechnology from "./TableTechnology";
import { startLoadingCatalogSectionsAll, startLoadingCatalogSectionsByLevel1 } from "../../app/actions/catalogSectionActions";
import { startLoadingCatalogPhasesAll, startLoadingCatalogPhasesByLevel1 } from "../../app/actions/catalogPhaseActions";
import { debugLogPages } from "../../app/helpers/debugHelper";
import Pagination from "../generic-ui/Pagination";
import { getSelectedCatalogsByDescriptionHelper, getSelectedProblemsOriginalCatalogsHelper, stringToListHelper } from "../../app/helpers/helperCatalogs";
import { buildFichaNavState } from "../../app/helpers/moduleFichaNavHelper";
import FlowModuleListPanel from "../specialist-flow-engine/FlowModuleListPanel";
import { isFlowEngineEnabledForProcess } from "../../app/helpers/flowEngineConfigHelper";
import useFlowModuleListFilters from "../../app/hooks/useFlowModuleListFilters";
import useCanOperateFlowListRecord from "../../app/hooks/useCanOperateFlowListRecord";
import { formatModuleListTitle } from "../../app/helpers/flowModuleListUiHelper";

const listOptions = [2, 3, 4, 5, 10, 15, 20, 25, 50, 100];

const ListTechnology = () => {
  debugLogPages(':: ListTechnology');

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { trackEvent } = useTracking();

  const {
    technologiesP,
    totalItemsTechnologyP,
    totalPagesTechnologyP,
    currentPageTechnologyP,
    technologiesS,
    totalItemsTechnologyS,
    totalPagesTechnologyS,
    currentPageTechnologyS
  } = useSelector((state) => state.technology);

  const { listProblems, listGeneralAdaptations, listStructuralAdaptations, listSpecificAdaptations } = useSelector((state) => state.catalog);

  const { isRoleSpecialistSelected } = useSelector((state) => state.userRole.roleLogin);
  const configuration = useSelector((state) => state.configuration);

  const [, setCurrentPageClick] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageInitial] = useState(0);
  const [searchText, setSearchText] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const flowSelectionEnabled = isFlowEngineEnabledForProcess(configuration, "technology");
  const useLegacySearch = Boolean((searchText || "").trim()) && !flowSelectionEnabled;
  const listRecords = useLegacySearch ? (technologiesS || []) : (technologiesP || []);
  const totalAssigned = useLegacySearch ? totalItemsTechnologyS : totalItemsTechnologyP;
  const currentPage = useLegacySearch ? currentPageTechnologyS : currentPageTechnologyP;
  const totalPages = useLegacySearch ? totalPagesTechnologyS : totalPagesTechnologyP;
  const totalItems = useLegacySearch ? totalItemsTechnologyS : totalItemsTechnologyP;

  const reloadTechnologyList = (page = pageInitial, size = itemsPerPage) => {
    if (useLegacySearch) {
      dispatch(startSearchingTechnologiesPaginated(searchText, page, size));
      return;
    }

    dispatch(startLoadingTechnologiesPaginated(page, size));
  };

  const {
    assignedOnly,
    showPending,
    showRejected,
    showFlowListFilter,
    flowFilterActive,
    showUserFilter,
    roleLabel,
    titleSuffix,
    setAssignedOnly,
    setShowPending,
    setShowRejected
  } = useFlowModuleListFilters({
    process: 'technology',
    isRoleSpecialistSelected,
    onReload: reloadTechnologyList,
    pageInitial,
    itemsPerPage,
    onFilterChange: () => setSelectedIds([])
  });
  const canOperateRecord = useCanOperateFlowListRecord();

  useEffect(() => {
    dispatch(startLoadingCatalogsAll());

    dispatch(startLoadingContinents());
    dispatch(startLoadingRegions());
    dispatch(startLoadingCountries());

    dispatch(startLoadingCatalogCiiusAll());
    dispatch(startLoadingCatalogSectionsAll());
    dispatch(startLoadingCatalogPhasesAll());

    dispatch(startLoadingCatalogCiiusByLevel1(1));
    dispatch(startLoadingCatalogSectionsByLevel1(1));
    dispatch(startLoadingCatalogPhasesByLevel1(1));

    dispatch(technologyCleanSearchText());

    return () => {
      dispatch(cleanTechnologiesPaginatedS());
      dispatch(technologyCleanSearchText());
    };
  }, []);

  const onEditRow = (row) => {
    trackEvent({
      component: "EditTechnology",
      method: "onEditRow",
      event: "EditTechnology-Clicked",
    });

    if (row.catalogcountries_id > 0) {
      dispatch(getContinentRegionCountryByCountryId(row.catalogcountries_id));
    }

    let defaultData = [];
    if (row && row.ModProblem_To_Technology && row.ModProblem_To_Technology.length > 0) {
      defaultData = getSelectedProblemsOriginalCatalogsHelper(row.ModProblem_To_Technology, listProblems);
      row.catalogproblem_list = defaultData;
    }

    //14 general adaptation, 15 structural adaptation, 16 specific adaptation
    let defaultDataGE = [];
    if (row && row.general_adaptation) {
      const general_adaptation_list = stringToListHelper(row.general_adaptation);
      defaultDataGE = getSelectedCatalogsByDescriptionHelper(general_adaptation_list, listGeneralAdaptations);
      row.general_adaptation_list = defaultDataGE;
      row.general_adaptation = defaultDataGE;
    }

    let defaultDataST = [];
    if (row && row.structural_adaptation) {
      const structural_adaptation_list = stringToListHelper(row.structural_adaptation);
      defaultDataST = getSelectedCatalogsByDescriptionHelper(structural_adaptation_list, listStructuralAdaptations);
      row.structural_adaptation_list = defaultDataST;
      row.structural_adaptation = defaultDataST;
    }

    let defaultDataSP = [];
    if (row && row.specific_adaptation) {
      const specific_adaptation_list = stringToListHelper(row.specific_adaptation);
      defaultDataSP = getSelectedCatalogsByDescriptionHelper(specific_adaptation_list, listSpecificAdaptations);
      row.specific_adaptation_list = defaultDataSP;
      row.specific_adaptation = defaultDataSP;
    }

    dispatch(selectTechnology(row));

    navigate("/specialist-technology/edit-technology", {
      state: buildFichaNavState("technology", {
        searchTextNavListUser: searchText
      })
    });
  };

  const onDeleteRow = (row) => {
    trackEvent({
      component: "DeleteTechnology",
      method: "onDeleteRow",
      event: "DeleteTechnology-Clicked",
    });
    dispatch(technologyStartDelete(row.id));
  };

  const onPageChange = (pageClick) => {
    const nextPage = Math.max(0, pageClick - 1);
    if (useLegacySearch) {
      dispatch(startSearchingTechnologiesPaginated(searchText, nextPage, itemsPerPage));
    } else {
      dispatch(startLoadingTechnologiesPaginated(nextPage, itemsPerPage));
    }
    setCurrentPageClick(pageClick);
  };

  const onChangeSelect = (event) => {
    const nextSize = Number(event.target.value) || 10;
    setItemsPerPage(nextSize);
    if (useLegacySearch) {
      dispatch(startSearchingTechnologiesPaginated(searchText, 0, nextSize));
    } else {
      dispatch(startLoadingTechnologiesPaginated(0, nextSize));
    }
    setCurrentPageClick(0);
  };

  const reloadList = () => {
    const page = Number(currentPage) || 0;
    if (useLegacySearch) {
      dispatch(startSearchingTechnologiesPaginated(searchText, page, itemsPerPage));
    } else {
      dispatch(startLoadingTechnologiesPaginated(page, itemsPerPage));
    }
    setSelectedIds([]);
  };

  const onToggleRow = (id) => {
    setSelectedIds((prev) => (
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    ));
  };

  const onToggleAll = () => {
    const operableIds = (listRecords || []).filter(canOperateRecord).map((row) => row.id);
    if (!operableIds.length) {
      return;
    }
    const allSelected = operableIds.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : operableIds);
  };

  debugLogPages(':: ListTechnology', { technologiesS });


  return (
    <>
      <NavbarIconsModuleContext moduleProcess="technology" mode="list" />

      <div className="d-flex justify-content-center page-title">{formatModuleListTitle(t("technology.page.list"), titleSuffix)}</div>

      <hr className="line-hr" />

      {!flowSelectionEnabled && (
        <SearchBack searchText={searchText} setSearchText={setSearchText} itemsPerPage={itemsPerPage} module={"technology"} />
      )}

      <FlowModuleListPanel
        moduleProcess="technology"
        moduleName={t("technology.page.list")}
        records={listRecords}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        onAfterAction={reloadList}
        totalAssigned={totalAssigned}
        showFlowListFilter={showFlowListFilter}
        flowFilterActive={flowFilterActive}
        assignedOnly={assignedOnly}
        showPending={showPending}
        showRejected={showRejected}
        roleLabel={roleLabel}
        onAssignedOnlyChange={setAssignedOnly}
        onShowPendingChange={setShowPending}
        onShowRejectedChange={setShowRejected}
        showUserFilter={showUserFilter}
      />

      <TableTechnology
        data={listRecords}
        onEditRow={onEditRow}
        onDeleteRow={onDeleteRow}
        flowSelectionEnabled={flowSelectionEnabled}
        selectedIds={selectedIds}
        onToggleRow={onToggleRow}
        onToggleAll={onToggleAll}
        canSelectRecord={canOperateRecord}
        onSelectionChange={setSelectedIds}
        onExcelApplied={reloadList}
      />

      <div>
        <Select
          listOptions={listOptions}
          onChangeSelect={onChangeSelect}
          pageSize={itemsPerPage}
        />
      </div>

      <div className="pagination justify-content-center">
        <Pagination
          totalPages={totalPages}
          totalItems={totalItems}
          currentPage={(Number(currentPage) || 0) + 1}
          itemsPerPage={itemsPerPage}
          onPageChange={onPageChange}
        />
      </div>
    </>
  );
};

export default ListTechnology;
